import React, { FC, useEffect, useState } from 'react';
import { Button, Drawer, Dropdown, Menu, Spin } from 'antd';
import { IconBackground } from 'common/components';
import { Copy, LegalEntity, Options } from 'common/svg';
import { EColor } from 'common/const';
import { LIST_LIMIT_0 } from 'common/config';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { IContract } from 'entities/Contract/Contract.models';
import { SettingsCreateCustomerIdModal } from 'entities/Settings/components';
import { ISettingsIntegrationsContractCustomer } from 'entities/Settings/Settings.models';
import { getAddressList, selectAddressList, selectIsAddressLoading, updateAddressBySeller } from 'entities/Address/Address.slice';
import { getLegalList, selectIsLegalLoading, selectLegalList, updateLegalBySeller } from 'entities/LegalEntity/Legal.slice';

enum ETab {
  LegalId = 'legal-id',
  ConsigneeId = 'consignee-id',
}

interface IComponentProps {
  open: boolean;
  contract: IContract | null;
  onClose: () => void;
}

export const SettingsCreateCustomerIdCard: FC<IComponentProps> = ({ open, contract, onClose }) => {
  const dispatch = useAppDispatch();
  // address
  const addressList = useAppSelector(selectAddressList);
  const isAddressLoading = useAppSelector(selectIsAddressLoading);
  // legal
  const legalList = useAppSelector(selectLegalList);
  const isLegalLoading = useAppSelector(selectIsLegalLoading);

  const [tab, setTab] = useState<ETab>(ETab.LegalId);
  const [customers, setCustomers] = useState<ISettingsIntegrationsContractCustomer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<ISettingsIntegrationsContractCustomer | null>(null);
  const [openSettingsCreateCustomerIdModal, setOpenSettingsCreateCustomerIdModal] = useState<boolean>(false);

  useEffect(() => {
    if (tab === ETab.LegalId) {
      const customers = legalList.map((legal) => {
        return {
          id: legal.id,
          name: legal.name,
          inn: legal.inn,
          kpp: legal.kpp,
          customerId: legal.customerId,
          type: 'legal',
        };
      });

      setCustomers(customers);
    } else {
      const customers = addressList.map((address) => {
        return {
          id: address.id,
          name: address.name,
          customerId: address.customerId,
          type: 'consignee',
        };
      });

      setCustomers(customers);
    }
  }, [tab, legalList, addressList]);

  const handleClose = () => {
    if (isLegalLoading) {
      return;
    }

    onClose();
    setTab(ETab.LegalId);
  };

  const onTabChange = (key: ETab) => {
    if (!contract) {
      return;
    }

    setTab(key as ETab);

    if (key === ETab.LegalId) {
      dispatch(
        getLegalList({
          accountId: contract.buyerId,
          limit: LIST_LIMIT_0,
        }),
      );
    } else {
      dispatch(
        getAddressList({
          accountId: contract.buyerId,
          limit: LIST_LIMIT_0,
        }),
      );
    }
  };

  const onUpdateCustomerClick = (id: number, value: string | null, onSuccess?: () => void) => {
    if (tab === ETab.LegalId) {
      dispatch(
        updateLegalBySeller({
          id,
          customerId: value,
          onSuccess,
        }),
      );
    } else {
      dispatch(
        updateAddressBySeller({
          id,
          customerId: value,
          onSuccess,
        }),
      );
    }
  };

  const copyCustomerId = (e: any, id?: string) => {
    e.stopPropagation();

    if (id) {
      navigator.clipboard.writeText(id);
    }
  };

  return (
    <>
      <Drawer rootClassName="redesign drawer" destroyOnClose width={720} open={open} onClose={handleClose}>
        {contract && <div className="mb-14 ff-regular fs-xxl">{contract.buyerName}</div>}

        <Menu
          items={[
            { key: ETab.LegalId, label: 'ID покупателя' },
            { key: ETab.ConsigneeId, label: 'ID грузополучателя' },
          ]}
          onClick={(e) => onTabChange(e.key as ETab)}
          selectedKeys={[tab]}
          mode="horizontal"
        />

        <Spin spinning={isAddressLoading || isLegalLoading}>
          <div className="pb-20">
            {customers.map((customer) => {
              return (
                <div key={customer.id} className="settings-create-customer-id-card__item-row">
                  <div className="flex align-center">
                    <div className="mr-6">
                      <IconBackground
                        content={<LegalEntity className="icon-legal-entity-dark-grey" />}
                        color={EColor.LightGrey}
                      />
                    </div>

                    <div className="flex-1">
                      <div className="mb-2 ff-medium fs-s" title={customer.name}>
                        {customer.name}
                      </div>

                      {customer.inn && <span className="ff-regular fs-xs color-dark-grey">{`ИНН: ${customer.inn}`}</span>}
                      {customer.kpp && <span className="ml-3 ff-regular fs-xs color-dark-grey">{`КПП: ${customer.kpp}`}</span>}
                    </div>
                  </div>

                  <div className="flex align-center">
                    {customer.customerId ? (
                      <>
                        <span className="mr-6 ff-medium fs-s ellipsis" style={{ maxWidth: 138 }} title={customer.customerId}>
                          {customer.customerId}
                        </span>

                        <div className="mr-6">
                          <Copy
                            className="icon-dark-grey cursor-pointer"
                            onClick={(e) => copyCustomerId(e, customer.customerId)}
                          />
                        </div>

                        <Dropdown
                          overlayClassName="redesign"
                          menu={{
                            items: [
                              {
                                key: 'edit',
                                label: 'Редактировать',
                                onClick: () => {
                                  setSelectedCustomer(customer);
                                  setOpenSettingsCreateCustomerIdModal(true);
                                },
                              },
                              { type: 'divider' },
                              {
                                key: 'delete',
                                label: 'Удалить ID',
                                className: 'red',
                                onClick: () => onUpdateCustomerClick(customer.id, null),
                              },
                            ],
                          }}
                        >
                          <Button
                            className="button-icon"
                            icon={<Options className="icon-options-dark-grey" style={{ transform: 'rotate(90deg)' }} />}
                          />
                        </Dropdown>
                      </>
                    ) : (
                      <Button
                        className="button-sm secondary"
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setOpenSettingsCreateCustomerIdModal(true);
                        }}
                      >
                        Добавить ID покупателя
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Spin>
      </Drawer>

      <SettingsCreateCustomerIdModal
        open={openSettingsCreateCustomerIdModal}
        loading={isAddressLoading || isLegalLoading}
        customer={selectedCustomer}
        onClose={() => setOpenSettingsCreateCustomerIdModal(false)}
        onSave={onUpdateCustomerClick}
      />
    </>
  );
};
